import React, { useEffect, useState } from "react";
import { JsonInput, MultiSelect, NumberInput, Select, Switch, TagsInput, Textarea, TextInput } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { FieldDescriptor } from "@utils/mappingConfig";
import { deepEqual } from "@utils/mappingConfig";
import { prettyJson } from "./format";

export interface ValueInputProps {
  descriptor: FieldDescriptor;
  value: unknown;
  onChange: (value: unknown) => void;
  readOnly?: boolean;
  error?: React.ReactNode;
  /** Label + description; omitted in table cells. */
  label?: React.ReactNode;
  description?: React.ReactNode;
  /** Compact (table cell) size. */
  compact?: boolean;
  /** Reports a value the input holds but cannot emit (invalid JSON). */
  onInvalid?: (message: string | null) => void;
}

const asOptions = (values: unknown[] | undefined) => (values ?? []).map((v) => ({ value: String(v), label: String(v) }));

/** Maps a select's string back to the enum member (enums may be numbers). */
const fromOption = (values: unknown[] | undefined, selected: string | null): unknown =>
  selected === null ? null : (values ?? []).find((v) => String(v) === selected) ?? selected;

const JsonField: React.FC<ValueInputProps> = ({ descriptor, value, onChange, readOnly, error, label, description, compact, onInvalid }) => {
  const { t } = useTranslation();
  const [text, setText] = useState(() => prettyJson(value));
  const [parseError, setParseError] = useState<string | null>(null);

  // Follow outside changes (a reload, a reset) unless they are what we just emitted.
  useEffect(() => {
    setText((current) => {
      try {
        if (deepEqual(JSON.parse(current), value)) return current;
      } catch {
        /* the text is invalid: an outside change replaces it */
      }
      return prettyJson(value);
    });
  }, [value]);

  const handle = (next: string) => {
    setText(next);
    try {
      const parsed: unknown = next.trim() === "" && descriptor.nullable ? null : JSON.parse(next);
      setParseError(null);
      onInvalid?.(null);
      onChange(parsed);
    } catch (err) {
      const message = t("mappingConfig.form.invalidJson", { error: err instanceof Error ? err.message : String(err) });
      setParseError(message);
      onInvalid?.(message);
    }
  };

  return (
    <JsonInput
      label={label}
      description={description}
      value={text}
      onChange={handle}
      readOnly={readOnly}
      error={parseError ?? error}
      autosize
      minRows={compact ? 1 : 4}
      maxRows={compact ? 6 : 20}
      size={compact ? "xs" : "sm"}
      styles={{ input: { fontFamily: "var(--xpf-font-mono)" } }}
    />
  );
};

/**
 * One input for a field kind that holds a single value (scalars, enums, string
 * lists, prompts, JSON). Types and constraints come from the field's schema.
 */
export const ValueInput: React.FC<ValueInputProps> = (props) => {
  const { descriptor, value, onChange, readOnly, error, label, description, compact } = props;
  const { schema, kind, nullable } = descriptor;
  const size = compact ? "xs" : "sm";
  const common = { label, description, error, size, readOnly } as const;

  switch (kind) {
    case "boolean":
      return (
        <Switch
          label={label}
          description={description}
          error={error}
          size={size}
          checked={value === true}
          disabled={readOnly}
          onChange={(e) => onChange(e.currentTarget.checked)}
        />
      );
    case "integer":
    case "number":
      return (
        <NumberInput
          {...common}
          value={typeof value === "number" ? value : ""}
          min={schema.minimum}
          max={schema.maximum}
          allowDecimal={kind === "number"}
          step={kind === "number" ? 0.1 : 1}
          onChange={(next) => onChange(next === "" ? null : typeof next === "number" ? next : Number(next))}
          placeholder={nullable ? "null" : undefined}
        />
      );
    case "enum":
      return (
        <Select
          {...common}
          data={asOptions(schema.enum)}
          value={value === null || value === undefined ? null : String(value)}
          onChange={(next) => onChange(fromOption(schema.enum, next))}
          clearable={nullable}
          allowDeselect={nullable}
          disabled={readOnly}
          placeholder={nullable ? "null" : undefined}
          comboboxProps={{ withinPortal: true, portalProps: { target: "#admin-portal-root" } }}
        />
      );
    case "enumArray":
      return (
        <MultiSelect
          {...common}
          data={asOptions(descriptor.itemSchema?.enum)}
          value={Array.isArray(value) ? value.map(String) : []}
          onChange={(next) => onChange(next.map((v) => fromOption(descriptor.itemSchema?.enum, v)))}
          disabled={readOnly}
          maxValues={schema.maxItems}
          searchable
          comboboxProps={{ withinPortal: true, portalProps: { target: "#admin-portal-root" } }}
        />
      );
    case "stringArray":
      return (
        <TagsInput
          {...common}
          value={Array.isArray(value) ? value.map(String) : []}
          onChange={onChange}
          disabled={readOnly}
          maxTags={schema.maxItems}
          comboboxProps={{ withinPortal: true, portalProps: { target: "#admin-portal-root" } }}
        />
      );
    case "prompt":
      return (
        <Textarea
          {...common}
          value={typeof value === "string" ? value : value === null || value === undefined ? "" : String(value)}
          onChange={(e) => onChange(e.currentTarget.value === "" && nullable ? null : e.currentTarget.value)}
          autosize
          minRows={compact ? 2 : 8}
          maxRows={compact ? 6 : 24}
          maxLength={schema.maxLength}
          styles={{ input: { fontFamily: "var(--xpf-font-mono)" } }}
        />
      );
    case "string":
      return (schema.maxLength ?? 0) > 256 && !compact ? (
        <Textarea
          {...common}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.currentTarget.value === "" && nullable ? null : e.currentTarget.value)}
          autosize
          minRows={2}
          maxRows={8}
          maxLength={schema.maxLength}
        />
      ) : (
        <TextInput
          {...common}
          value={typeof value === "string" ? value : value === null || value === undefined ? "" : String(value)}
          onChange={(e) => onChange(e.currentTarget.value === "" && nullable ? null : e.currentTarget.value)}
          maxLength={schema.maxLength}
          placeholder={nullable ? "null" : undefined}
        />
      );
    default:
      return <JsonField {...props} />;
  }
};
